import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export const TrackingStageDialog = ({ container, stages, currentStage, onDone }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const idx = stages.findIndex((s) => s.code === currentStage);
  const next = stages[idx + 1]?.code || stages[stages.length - 1].code;
  const [form, setForm] = useState({ event_type: next, event_date: new Date().toISOString().slice(0, 10), location: "", vessel: container.vessel || "", voyage: container.voyage || "", note: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/containers/${container.id}/tracking/stage`, form);
      if (r.data.late_historical) toast.info("Historical event recorded — current stage was not regressed");
      else if (r.data.status === "FLAGGED") toast.warning("Stage recorded with warnings — review required");
      else toast.success("Stage recorded — audited");
      setOpen(false);
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm((f) => ({ ...f, event_type: next })); }}>
      <DialogTrigger asChild>
        <Button data-testid="record-stage-button" className="h-10 bg-terracotta text-white hover:bg-terracotta-hover">Record shipment stage</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md" data-testid="record-stage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-charcoal">Record shipment stage</DialogTitle>
          <DialogDescription className="text-sm text-warmgrey">Stages never create or move inventory — warehouse receiving stays a separate action. Earlier (historical) events are kept in the ledger without regressing the current stage.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="record-stage-form">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Stage *</Label>
            <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
              <SelectTrigger data-testid="stage-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {stages.map((s) => <SelectItem key={s.code} value={s.code} data-testid={`stage-option-${s.code}`}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Actual date *</Label><DatePickerField required value={form.event_date} onChange={(v) => setForm((f) => ({ ...f, event_date: v }))} testId="stage-date-input" /></div>
            <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Location</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Casablanca / Miami" data-testid="stage-location-input" className="h-11 bg-white" /></div>
            <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Vessel</Label><Input value={form.vessel} onChange={(e) => setForm((f) => ({ ...f, vessel: e.target.value }))} data-testid="stage-vessel-input" className="h-11 bg-white" /></div>
            <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Voyage</Label><Input value={form.voyage} onChange={(e) => setForm((f) => ({ ...f, voyage: e.target.value }))} data-testid="stage-voyage-input" className="h-11 bg-white" /></div>
          </div>
          <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Note</Label><Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="stage-note-input" className="h-11 bg-white" /></div>
          <Button type="submit" disabled={saving} data-testid="stage-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record stage (audited)"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
